import { ZERO, add, clampPos, minM, perUnitFloor, share, sub, type Money } from './money.js';
import { addMonths, diffDays, type PlainDate } from './date.js';
import { conservativeIncome, obligationAmount } from './available.js';
import { monthlyObligations } from './instances.js';
import { monthlyNeedFor } from './goals.js';
import { hasExpensiveDebt } from './loans.js';
import { bufferStage } from './plan.js';
import type { Goal, IncomeStream, Loan, Obligation } from './types.js';

export interface VerdictInput {
  today: PlainDate;
  incomeStreams: IncomeStream[];
  obligations: Obligation[];
  /** Обычные траты на повседневные нужды помимо обязательств. */
  needBaseline: Money;
  /** Обычные необязательные расходы. */
  wantBaseline: Money;
  goals: Goal[];
  /**
   * Долги. От их ставок зависит, что рекомендовать: гасить долг раньше
   * подушки стоит тогда, когда он дорогой. Ипотека под умеренный процент
   * такого не требует, и обещать обратное было бы неправдой.
   */
  loans?: Loan[];
}

export type LeverKind = 'shift_dates' | 'queue_goals' | 'cut_want';

export interface Lever {
  kind: LeverKind;
  /** Что предлагаем сделать — текст показывается пользователю как есть. */
  label: string;
  /** Насколько это закрывает дефицит. */
  covers: Money;
  /** Рычаг реально применим: нечего сдвигать — нечего и предлагать. */
  applicable: boolean;
}

export interface ScenarioGoal {
  goalId: string;
  name: string;
  /** Когда цель будет достигнута при этом сценарии. */
  eta: PlainDate | null;
  monthly: Money;
}

export interface Scenario {
  key: 'debt_first' | 'balanced' | 'buffer_first';
  name: string;
  /** Почему так — объяснение, а не просто набор дат. */
  reason: string;
  goals: ScenarioGoal[];
  recommended: boolean;
}

export interface VerdictResult {
  income: Money;
  obligationsMonthly: Money;
  /** Обязательства плюс базовая жизнь: ниже этого не проживёшь. */
  needTotal: Money;
  /** Что остаётся после обязательного — за это конкурируют цели и «хочу». */
  freeCash: Money;
  /** Сколько нужно в месяц, чтобы успеть по всем целям к их срокам. */
  goalsRequired: Money;
  /** Положительный — план не сходится. */
  deficit: Money;
  feasible: boolean;
  /** Свободных денег почти нет — проценты обсуждать бессмысленно. */
  trapped: boolean;
  levers: Lever[];
  scenarios: Scenario[];
}

const TRAP_SHARE = 0.05;
const MAX_MONTHS = 120;

/**
 * Вердикт онбординга (PRD §4.9).
 *
 * Никогда не отвечает «недостижимо». Считает, что достижимо и чего это стоит,
 * потому что «ты не можешь» человек читает как отказ и закрывает приложение,
 * а «вот что можешь» — как помощь. Конфликт всегда показывается числом:
 * «цели требуют 97 000, свободно 54 000, дефицит 43 000».
 */
export function computeVerdict(input: VerdictInput): VerdictResult {
  const income = add(...input.incomeStreams.filter((s) => s.isActive).map(conservativeIncome));
  // По календарю наступлений: за месяц может наступить несколько недельных
  // платежей и ни одного годового.
  const obligationsMonthly = monthlyObligations(input.obligations, input.today);
  const needTotal = add(obligationsMonthly, input.needBaseline);
  const freeCash = clampPos(sub(income, needTotal));

  const active = input.goals.filter((g) => g.status === 'active');
  const goalsRequired = add(...active.map((g) => requiredMonthly(g, input.today)));
  const deficit = clampPos(sub(goalsRequired, freeCash));
  const feasible = deficit === ZERO;
  const trapped = freeCash < share(income, TRAP_SHARE);

  return {
    income, obligationsMonthly, needTotal, freeCash, goalsRequired, deficit,
    feasible, trapped,
    levers: buildLevers(input, deficit, active),
    scenarios: buildScenarios(input, freeCash, active),
  };
}

/**
 * Сколько нужно в месяц, чтобы успеть к сроку. Без срока — заявленный взнос.
 *
 * Здесь была своя копия расчёта, отличавшаяся обработкой прошедшего срока.
 * Две формулы «сколько в месяц» неизбежно разъезжаются, и разъехались бы ещё
 * сильнее с появлением даты начала, поэтому осталась одна.
 */
export const requiredMonthly = monthlyNeedFor;

/**
 * Доживу ли до зарплаты.
 *
 * Вердикт после онбординга отвечает на месячный вопрос: сходятся ли доход,
 * обязательства и цели. Но первый вопрос человека другой и ближе — хватит ли
 * того, что лежит на счетах, до ближайшего прихода. Это разные горизонты, и
 * ответ на один не заменяет ответа на другой.
 *
 * Целей здесь нет намеренно: цель откладывается из будущего дохода, а не из
 * остатка недожитого цикла.
 */
export interface CycleOutlook {
  /** Сколько остаётся после всех вычетов этого цикла. */
  available: Money;
  /** Дней до ближайшего прихода. */
  days: number;
  /** Сколько нужно повседневные нужды за эти дни по базовым расходам. */
  livingNeed: Money;
  /** Хватает ли повседневные нужды до прихода. */
  covered: boolean;
  /** Чего не хватает, если не хватает. */
  shortfall: Money;
  /** Сколько можно тратить в день. При нехватке — ноль. */
  perDay: Money;
}

export function computeCycleOutlook(input: {
  available: Money;
  days: number;
  needBaseline: Money;
}): CycleOutlook {
  const days = Math.max(1, Math.trunc(input.days));
  // Месяц в базовых расходах — тридцать дней: человек называл сумму «в месяц»,
  // и растягивать её по календарю точнее, чем он сам её оценил, — самообман.
  const livingNeed = share(input.needBaseline, days / 30);
  const covered = input.available >= livingNeed;
  return {
    available: input.available,
    days,
    livingNeed,
    covered,
    shortfall: clampPos(sub(livingNeed, input.available)),
    perDay: input.available > ZERO ? perUnitFloor(input.available, days, 100) : ZERO,
  };
}

/**
 * Три рычага, всегда в этом порядке — от самого безболезненного к неприятному.
 * Четвёртый рычаг (больше зарабатывать) существует, но мы его не произносим:
 * это чтение морали, а не финансовый совет.
 */
function buildLevers(input: VerdictInput, deficit: Money, goals: Goal[]): Lever[] {
  if (deficit === ZERO) return [];

  // 1. Сдвинуть даты: тот же остаток, но растянутый на больший срок.
  const shiftable = goals.filter((g) => g.targetDate && g.priority !== 'critical');
  const afterShift = add(...goals.map((g) => {
    const gap = clampPos(sub(g.targetAmount, g.currentAmount));
    if (!g.targetDate || g.priority === 'critical') return requiredMonthly(g, input.today);
    const months = Math.max(1, Math.round(diffDays(input.today, g.targetDate) / 30));
    return share(gap, 1 / (months + 6)); // сдвиг на полгода
  }));
  const shiftCovers = clampPos(sub(add(...goals.map((g) => requiredMonthly(g, input.today))), afterShift));

  // 2. Поставить цели в очередь: не «удалить», а «вернёмся позже».
  const queueable = [...goals]
    .filter((g) => g.priority === 'medium' || g.priority === 'low')
    .sort((a, b) => requiredMonthly(b, input.today) - requiredMonthly(a, input.today));
  let queued = ZERO;
  const queuedNames: string[] = [];
  for (const g of queueable) {
    if (queued >= deficit) break;
    queued = add(queued, requiredMonthly(g, input.today));
    queuedNames.push(g.name);
  }

  return [
    {
      kind: 'shift_dates',
      label: shiftable.length > 0
        ? `Сдвинуть сроки на полгода — освободит ${fmt(shiftCovers)} в месяц`
        : 'Сдвигать нечего: у целей нет сроков',
      covers: minM(shiftCovers, deficit),
      applicable: shiftable.length > 0 && shiftCovers > ZERO,
    },
    {
      kind: 'queue_goals',
      label: queuedNames.length > 0
        ? `Отложить в очередь: ${queuedNames.join(', ')} — освободит ${fmt(minM(queued, deficit))}`
        : 'В очередь ставить нечего: все цели важные',
      covers: minM(queued, deficit),
      applicable: queuedNames.length > 0,
    },
    {
      kind: 'cut_want',
      label: input.wantBaseline > ZERO
        ? `Сократить необязательные траты на ${fmt(minM(deficit, input.wantBaseline))} из ${fmt(input.wantBaseline)} — реалистично?`
        : 'Необязательные траты не заданы — сокращать нечего',
      covers: minM(deficit, input.wantBaseline),
      applicable: input.wantBaseline > ZERO,
    },
  ];
}

/**
 * Сценарии с конкретными датами по каждой цели.
 *
 * Сравнение дат работает, абстрактный слайдер «агрессивность плана» — нет:
 * «подушка к марту или отпуск в июне» — это выбор, который человек может
 * сделать, а «60 на 40» — нет.
 */
function buildScenarios(input: VerdictInput, freeCash: Money, goals: Goal[]): Scenario[] {
  const hasDebt = goals.some((g) => g.kind === 'debt_payoff');
  const loans = input.loans ?? [];
  const loansById = new Map(loans.map((l) => [l.id, l]));
  const rateOf = (g: Goal): number => (g.loanId ? loansById.get(g.loanId)?.ratePct ?? 0 : 0);
  // Дорогой долг стоит гасить раньше подушки — это арифметика. Ипотека под
  // умеренный процент такого не требует: там подушка важнее. Пока список
  // долгов не передан, поведение прежнее — любой долг считается срочным.
  const debtIsUrgent = loans.length === 0 ? hasDebt : hasDebt && hasExpensiveDebt(loans);
  const buffer = goals.find((g) => g.kind === 'emergency_fund');
  // Порог подушки считается от месячных расходов: обязательства плюс жизнь.
  const monthlyNeed = add(monthlyObligations(input.obligations, input.today), input.needBaseline);
  const bufferBehind = buffer
    ? bufferStage(buffer.currentAmount, monthlyNeed) !== 'done'
    : false;

  const order = (key: Scenario['key']) => (a: Goal, b: Goal): number => {
    const rank = (g: Goal): number => {
      if (key === 'debt_first') {
        if (g.kind === 'debt_payoff') return 0;
        if (g.kind === 'emergency_fund') return 1;
      }
      if (key === 'buffer_first') {
        if (g.kind === 'emergency_fund') return 0;
        if (g.kind === 'debt_payoff') return 1;
      }
      if (key === 'balanced') {
        if (g.kind === 'debt_payoff') return 0;
        if (g.kind === 'emergency_fund') return 0;
      }
      return { critical: 2, high: 3, medium: 4, low: 5 }[g.priority];
    };
    // Между собой долги идут по ставке: дороже — раньше. Человек с двумя
    // долгами должен видеть ту же очередь, что и на экране «Счета».
    if (a.kind === 'debt_payoff' && b.kind === 'debt_payoff') {
      return rank(a) - rank(b) || rateOf(b) - rateOf(a);
    }
    return rank(a) - rank(b);
  };

  const make = (key: Scenario['key'], name: string, reason: string, recommended: boolean): Scenario => ({
    key, name, reason, recommended,
    goals: simulate(goals, freeCash, order(key), input.today, key === 'balanced'),
  });

  return [
    make('debt_first', 'Сначала закрыть долг',
      'Долг под высокую ставку дороже любой цели по накоплению — это арифметика, а не предпочтение',
      debtIsUrgent),
    make('balanced', 'Сбалансированно',
      'Долг и подушка получают деньги одновременно, остальные цели — из остатка',
      !debtIsUrgent && !bufferBehind),
    make('buffer_first', 'Быстро к подушке',
      'Месяц расходов в запасе снимает необходимость снова лезть в кредитку',
      !debtIsUrgent && bufferBehind),
  ];
}

/**
 * Помесячное моделирование: каждый месяц свободные деньги идут по порядку
 * сценария. Считаем по месяцам, а не делением, потому что после закрытия
 * одной цели её взнос переходит следующей — и сроки сдвигаются.
 */
function simulate(
  goals: Goal[],
  freeCash: Money,
  cmp: (a: Goal, b: Goal) => number,
  today: PlainDate,
  parallel: boolean,
): ScenarioGoal[] {
  const state = goals.map((g) => ({
    goal: g,
    left: clampPos(sub(g.targetAmount, g.currentAmount)),
    eta: null as PlainDate | null,
    got: ZERO,
  }));
  const sorted = [...state].sort((a, b) => cmp(a.goal, b.goal));
  if (freeCash <= ZERO) {
    return state.map((s) => ({ goalId: s.goal.id, name: s.goal.name, eta: null, monthly: ZERO }));
  }

  for (let month = 1; month <= MAX_MONTHS; month++) {
    let budget = freeCash;
    const open = sorted.filter((s) => s.left > ZERO);
    if (open.length === 0) break;

    // Сбалансированный сценарий делит деньги между первыми по порядку целями,
    // остальные — строго по очереди.
    const receivers = parallel ? open.slice(0, Math.min(2, open.length)) : open.slice(0, 1);
    for (const s of receivers) {
      if (budget <= ZERO) break;
      const portion = parallel && receivers.length > 1
        ? minM(share(freeCash, 1 / receivers.length), budget)
        : budget;
      const pay = minM(portion, s.left);
      s.left = sub(s.left, pay);
      s.got = add(s.got, pay);
      budget = sub(budget, pay);
      if (s.left === ZERO && s.eta === null) s.eta = addMonths(today, month);
    }
    // Остаток месяца отдаём следующим открытым целям.
    for (const s of open) {
      if (budget <= ZERO) break;
      if (receivers.includes(s)) continue;
      const pay = minM(budget, s.left);
      s.left = sub(s.left, pay);
      s.got = add(s.got, pay);
      budget = sub(budget, pay);
      if (s.left === ZERO && s.eta === null) s.eta = addMonths(today, month);
    }
  }

  return state.map((s) => ({
    goalId: s.goal.id,
    name: s.goal.name,
    eta: s.eta,
    monthly: s.eta ? share(sub(s.goal.targetAmount, s.goal.currentAmount), 1 / monthsBetween(today, s.eta)) : ZERO,
  }));
}

const monthsBetween = (a: PlainDate, b: PlainDate): number =>
  Math.max(1, Math.round(diffDays(a, b) / 30));

const fmt = (m: Money): string => `${Math.round(m / 100).toLocaleString('ru-RU')} ₽`;
